/** @format */

import {
	Avatar,
	Box,
	Button,
	Card,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Pagination,
	Stack,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import NotificationModal from "../modal/notification-modal/NotificationModal";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import { colors } from "../../utility/types/constant";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { AppDispatch } from "../../redux/store/store";
import {
	fetchCircleInvitations,
	acceptCircleInvitation,
	denyCircleInvitation,
} from "../../redux/actions/circleInvitationAction";
import {
	selectCircleInvitations,
	selectCircleInvitationIsLoading,
	selectCircleInvitationError,
} from "../../redux/selectors/circleInvitationSelector";

import { CircleInvitationItem } from "../../redux/reducers/circleInvitationReducer";
import InvitationCard from "./circle-invitations/InvitationCard";
import { needsInviteeAction } from "./circle-invitations/invitationStatus";
import { selectCurrentUser } from "../../redux/selectors/userSelector";

const FONT = "'Montserrat', sans-serif";
const PAGE_SIZE = 5;

type InvitationTab = "pending" | "past";

const ViewCircleInvitations = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	const invitations = useSelector(selectCircleInvitations);
	const isLoading = useSelector(selectCircleInvitationIsLoading);
	const reduxError = useSelector(selectCircleInvitationError);
	const currentUser = useSelector(selectCurrentUser);
	const isUserVerified = currentUser?.isVerified === true;

	const [openNav, setOpenNav] = useState(false);
	const [selectedTab, setSelectedTab] = useState<InvitationTab | null>(null);
	const [page, setPage] = useState(1);
	const listTopRef = useRef<HTMLDivElement>(null);
	// Target is kept separately from `open` so the dialog text doesn't flicker
	// while it animates closed.
	const [denyTarget, setDenyTarget] = useState<CircleInvitationItem | null>(
		null,
	);
	const [isDenyDialogOpen, setIsDenyDialogOpen] = useState(false);

	// Notification modal state
	const [showNotificationModal, setShowNotificationModal] = useState(false);
	const [notificationTitle, setNotificationTitle] = useState("");
	const [notificationModalContent, setNotificationModalContent] = useState("");
	const [notifyCircle, setNotifyCircle] = useState({ name: "", image: "" });
	const [timeOutId, setTimeOutId] = useState<any>(null);
	const [actionLoading, setActionLoading] = useState<{
		invitationId: string | null;
		action: "accept" | "deny" | null;
	}>({
		invitationId: null,
		action: null,
	});

	useEffect(() => {
		dispatch(fetchCircleInvitations());
	}, [dispatch]);

	// Newest first, split into "needs response" and everything else
	const { pendingInvitations, pastInvitations } = useMemo(() => {
		const sorted = [...invitations]
			.filter((inv) => inv && inv._id)
			.sort(
				(a, b) =>
					new Date(b.created_At || 0).getTime() -
					new Date(a.created_At || 0).getTime(),
			);
		return {
			pendingInvitations: sorted.filter((inv) =>
				needsInviteeAction(inv.status),
			),
			pastInvitations: sorted.filter(
				(inv) => !needsInviteeAction(inv.status),
			),
		};
	}, [invitations]);

	const totalCount = pendingInvitations.length + pastInvitations.length;

	// Open on whatever needs attention until the user picks a tab themselves
	const activeTab: InvitationTab =
		selectedTab ?? (pendingInvitations.length > 0 ? "pending" : "past");

	const tabInvitations =
		activeTab === "pending" ? pendingInvitations : pastInvitations;
	const pageCount = Math.max(1, Math.ceil(tabInvitations.length / PAGE_SIZE));
	// Clamp so accepting/denying the last card on a page never leaves an empty page
	const currentPage = Math.min(page, pageCount);
	const pageStart = (currentPage - 1) * PAGE_SIZE;
	const shownInvitations = tabInvitations.slice(pageStart, pageStart + PAGE_SIZE);

	useEffect(() => {
		if (page > pageCount) setPage(pageCount);
	}, [page, pageCount]);

	// One action at a time: actionLoading tracks a single invitation
	const isAnyActionInFlight = actionLoading.invitationId !== null;

	const handleTabChange = (_event: React.SyntheticEvent, value: InvitationTab) => {
		setSelectedTab(value);
		setPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
		listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const handleShowNotificationModal = (title: string, message: string) => {
		setNotificationTitle(title);
		setNotificationModalContent(message);
		setShowNotificationModal(true);
	};

	const handleCloseNotificationModal = () => {
		setShowNotificationModal(false);
		setNotificationTitle("");
		setNotificationModalContent("");
		if (timeOutId) clearTimeout(timeOutId);
	};

	const handleAcceptInvitation = async (invitation: CircleInvitationItem) => {
		try {
			setActionLoading({ invitationId: invitation._id, action: "accept" });
			const result = await dispatch(
				acceptCircleInvitation({
					invitationId: invitation._id,
					token: invitation.tokenHash,
				}),
			).unwrap();

			const { message, invitation: updatedInvitation } = result;
			const circleImage = invitation.circle?.circleImage || "";
			const circleName = invitation.circle?.circleName || "the circle";

			setNotifyCircle({ name: circleName, image: circleImage });

			if (updatedInvitation?.membershipCreated) {
				handleShowNotificationModal("Invitation Accepted", message);
				const newTimeOutId = setTimeout(() => {
					setShowNotificationModal(false);
					navigate(`/circle/${invitation.circleId}`);
				}, 8000);
				setTimeOutId(newTimeOutId);
			} else {
				handleShowNotificationModal("Invitation Accepted", message);
				const newTimeOutId = setTimeout(() => {
					setShowNotificationModal(false);
				}, 5000);
				setTimeOutId(newTimeOutId);
			}

			// Refresh invitations list
			dispatch(fetchCircleInvitations());
		} catch (err: any) {
			const errorMessage =
				typeof err === "string" ? err : (
					err?.message || "Failed to accept invitation"
				);
			handleShowNotificationModal("Error", errorMessage);
			// The invitation may have changed (expired, rejected, cycle started); resync.
			dispatch(fetchCircleInvitations());
		} finally {
			setActionLoading({ invitationId: null, action: null });
		}
	};

	const handleDenyInvitation = async (invitation: CircleInvitationItem) => {
		try {
			setActionLoading({ invitationId: invitation._id, action: "deny" });
			await dispatch(
				denyCircleInvitation({
					invitationId: invitation._id,
					token: invitation.tokenHash,
				}),
			).unwrap();

			const circleName = invitation.circle?.circleName || "the circle";
			const circleImage = invitation.circle?.circleImage || "";

			setNotifyCircle({ name: circleName, image: circleImage });
			handleShowNotificationModal(
				"Invitation Declined",
				`You declined to join ${circleName}. No further action is needed.`,
			);

			// Refresh invitations list
			dispatch(fetchCircleInvitations());
		} catch (err: any) {
			const errorMessage =
				typeof err === "string" ? err : (
					err?.message || "Failed to deny invitation"
				);
			handleShowNotificationModal("Error", errorMessage);
			dispatch(fetchCircleInvitations());
		} finally {
			setActionLoading({ invitationId: null, action: null });
		}
	};

	const handleRequestDeny = (invitation: CircleInvitationItem) => {
		if (isAnyActionInFlight) return;
		setDenyTarget(invitation);
		setIsDenyDialogOpen(true);
	};

	const handleConfirmDeny = () => {
		setIsDenyDialogOpen(false);
		if (denyTarget) handleDenyInvitation(denyTarget);
	};

	const pageShell = (content: React.ReactNode) => (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				{content}
			</Box>
		</>
	);

	// ── Loading state ───────────────────────────────────────────────
	if (isLoading && invitations.length === 0) {
		return pageShell(
			<Container
				sx={{
					display: "flex",
					justifyContent: "center",
					alignContent: "center",
					alignItems: "center",
					maxHeight: "100%",
					mt: 12,
				}}>
				<LoadingSpinner loading={true} size={70} sx={{ position: "absolute" }} />
			</Container>,
		);
	}

	// ── Empty state ─────────────────────────────────────────────────
	if (totalCount === 0) {
		return pageShell(
			<Container
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					maxHeight: "100%",
					mt: 12,
				}}>
				<Card
					sx={{
						mt: 10,
						p: 5,
						maxWidth: 480,
						width: "100%",
						textAlign: "center",
						borderRadius: 3,
						boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
					}}>
					<Box
						sx={{
							width: 80,
							height: 80,
							borderRadius: "50%",
							backgroundColor: "rgba(2, 48, 32, 0.08)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mx: "auto",
							mb: 3,
						}}>
						<MailOutlineIcon sx={{ fontSize: 40, color: colors.secondPrimary }} />
					</Box>
					<Typography
						variant="h5"
						sx={{
							fontFamily: FONT,
							fontWeight: 700,
							mb: 1.5,
							color: "text.primary",
						}}>
						No Invitations Yet
					</Typography>
					<Typography
						variant="body1"
						sx={{
							fontFamily: FONT,
							color: "text.secondary",
							lineHeight: 1.6,
						}}>
						{reduxError ||
							"You haven't received any circle invitations so far. When someone invites you to join their circle, it will appear here."}
					</Typography>
				</Card>
			</Container>,
		);
	}

	// ── Card view ───────────────────────────────────────────────────
	return (
		<>
			{pageShell(
				<Container
					maxWidth="lg"
					sx={{
						mt: { xs: 11, lg: 12 },
						pb: { xs: 4, sm: 6 },
						px: { xs: 2, sm: 3 },
					}}>
					{/* Page header */}
					<Box sx={{ mb: { xs: 2, sm: 3 } }}>
						<Typography
							component="h1"
							sx={{
								fontFamily: FONT,
								fontSize: { xs: "1.35rem", sm: "1.6rem" },
								fontWeight: 800,
								color: "text.primary",
							}}>
							Circle Invitations
						</Typography>
						<Typography
							sx={{
								fontFamily: FONT,
								fontSize: { xs: "0.88rem", sm: "0.95rem" },
								color: "text.secondary",
								mt: 0.5,
							}}>
							{pendingInvitations.length > 0 ?
								`You have ${pendingInvitations.length} invitation${pendingInvitations.length > 1 ? "s" : ""} waiting for your response.`
							:	"You're all caught up. Past invitations are listed below."}
						</Typography>
					</Box>

					{/* Tabs */}
					<Tabs
						ref={listTopRef}
						value={activeTab}
						onChange={handleTabChange}
						variant="fullWidth"
						aria-label="Invitation filters"
						sx={{
							scrollMarginTop: { xs: 88, lg: 104 },
							mb: { xs: 2, sm: 3 },
							maxWidth: { sm: 420 },
							minHeight: 44,
							p: 0.5,
							borderRadius: "14px",
							backgroundColor: "rgba(15, 23, 42, 0.05)",
							"& .MuiTabs-indicator": { display: "none" },
							"& .MuiTab-root": {
								minHeight: 40,
								borderRadius: "10px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								fontSize: "0.88rem",
								color: "text.secondary",
								transition: "background-color 0.2s ease, color 0.2s ease",
							},
							"& .MuiTab-root.Mui-selected": {
								color: colors.secondPrimary,
								backgroundColor: "#fff",
								boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
							},
						}}>
						<Tab
							value="pending"
							label={`Needs response (${pendingInvitations.length})`}
						/>
						<Tab value="past" label={`Past (${pastInvitations.length})`} />
					</Tabs>

					{/* Cards */}
					{shownInvitations.length > 0 ?
						<Box
							sx={{
								display: "grid",
								gap: { xs: 2, sm: 2.5 },
								gridTemplateColumns: {
									xs: "1fr",
									md: "repeat(2, minmax(0, 1fr))",
									xl: "repeat(3, minmax(0, 1fr))",
								},
							}}>
							{shownInvitations.map((invitation, index) => {
								const isThisCard =
									actionLoading.invitationId === invitation._id;
								return (
									<InvitationCard
										key={invitation._id}
										invitation={invitation}
										index={index}
										isBusy={isAnyActionInFlight}
										isAcceptLoading={
											isThisCard && actionLoading.action === "accept"
										}
										isDenyLoading={
											isThisCard && actionLoading.action === "deny"
										}
										onAccept={handleAcceptInvitation}
										onDeny={handleRequestDeny}
										isUserVerified={isUserVerified}
										onOpenCircle={(circleId) =>
											navigate(`/circle/${circleId}`)
										}
										onVerifyIdentity={() =>
											navigate(
												currentUser?._id ? `/user/${currentUser._id}` : "/",
											)
										}
									/>
								);
							})}
						</Box>
					:	<Box
							sx={{
								py: 6,
								px: 3,
								textAlign: "center",
								borderRadius: "20px",
								border: "1px dashed rgba(15, 23, 42, 0.15)",
							}}>
							<TaskAltRoundedIcon
								sx={{ fontSize: 40, color: colors.mainSecondary, mb: 1 }}
							/>
							<Typography
								sx={{ fontFamily: FONT, fontWeight: 700, color: "text.primary" }}>
								{activeTab === "pending" ?
									"No invitations need your response"
								:	"No past invitations yet"}
							</Typography>
							{activeTab === "pending" && pastInvitations.length > 0 && (
								<Button
									onClick={(e) => handleTabChange(e, "past")}
									sx={{
										mt: 1.5,
										textTransform: "none",
										fontFamily: FONT,
										fontWeight: 700,
										color: colors.blueRibbon,
									}}>
									View past invitations
								</Button>
							)}
						</Box>
					}

					{pageCount > 1 && (
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems="center"
							justifyContent="space-between"
							spacing={1.5}
							sx={{ mt: 3 }}>
							<Typography
								sx={{
									fontFamily: FONT,
									fontSize: "0.85rem",
									color: "text.secondary",
								}}>
								Showing {pageStart + 1}–{pageStart + shownInvitations.length} of{" "}
								{tabInvitations.length}
							</Typography>
							<Pagination
								page={currentPage}
								count={pageCount}
								onChange={handlePageChange}
								shape="rounded"
								siblingCount={0}
								boundaryCount={1}
								aria-label="Invitation pages"
								sx={{
									"& .MuiPaginationItem-root": {
										minWidth: 40,
										height: 40,
										borderRadius: "10px",
										fontFamily: FONT,
										fontWeight: 700,
									},
									"& .MuiPaginationItem-root.Mui-selected": {
										color: "#fff",
										backgroundColor: colors.secondPrimary,
										"&:hover": { backgroundColor: "#03462f" },
									},
								}}
							/>
						</Stack>
					)}
				</Container>,
			)}

			{/* Deny confirmation — declining can't be undone */}
			<Dialog
				open={isDenyDialogOpen}
				onClose={() => setIsDenyDialogOpen(false)}
				TransitionProps={{ onExited: () => setDenyTarget(null) }}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: "20px", m: 2 } }}>
				<DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, pb: 1 }}>
					Decline this invitation?
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontFamily: FONT, color: "text.secondary", lineHeight: 1.6 }}>
						You won't be able to join{" "}
						<strong>{denyTarget?.circle?.circleName || "this circle"}</strong>{" "}
						with this invitation. You'd need a new invite to join later.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button
						onClick={() => setIsDenyDialogOpen(false)}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							color: "text.secondary",
						}}>
						Keep invitation
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={handleConfirmDeny}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							backgroundColor: colors.persianRed,
							"&:hover": { backgroundColor: "#b02b2a" },
						}}>
						Decline
					</Button>
				</DialogActions>
			</Dialog>

			{showNotificationModal && (
				<NotificationModal
					open={showNotificationModal}
					title={notificationTitle}
					handleClose={handleCloseNotificationModal}
					sx={{
						width: "auto",
						maxWidth: "100%",
						margin: "0",
					}}>
					<Grid
						container
						spacing={2}
						direction="row"
						alignItems="center"
						justifyContent="center"
						sx={{
							margin: "0",
							width: "100%",
							padding: "0",
						}}>
						<Grid item sx={{ maxWidth: "100%", flexBasis: "auto" }}>
							<Avatar
								src={notifyCircle.image}
								sx={{ width: 60, height: 60 }}
								alt="Circle"
							/>
						</Grid>
						<Grid item xs={9}>
							<Typography
								variant="body2"
								sx={{
									fontSize: 20,
									color: "text.secondary",
									overflow: "hidden",
									overflowWrap: "break-word",
									maxWidth: "100%",
								}}>
								{notificationModalContent}
							</Typography>
						</Grid>
					</Grid>
				</NotificationModal>
			)}
		</>
	);
};

export default ViewCircleInvitations;
